'use client';

import { usePathname, useRouter, useSearchParams } from 'next/navigation';

export type QueryParamValue = string | number | null;

export interface QueryParams {
  [key: string]: QueryParamValue;
}

interface QueryParamsOptions<T> {
  defaultValues: { [K in keyof T]: T[K] };
  validTypes?: { [K in keyof T]?: readonly T[K][] };
  removeDefaultValues?: boolean;
}

const useQueryParams = <T extends QueryParams>(
  options?: QueryParamsOptions<T>
) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const router = useRouter();

  const {
    defaultValues = {} as Partial<T>,
    validTypes = {} as Partial<T>,
    removeDefaultValues = true,
  } = options || {};

  const updateQueryString = ({ params }: { params: Partial<T> }) => {
    const currentParams = Object.fromEntries(searchParams.entries());
    const mergedParams = { ...currentParams, ...params };

    const newSearchParams = new URLSearchParams();
    Object.entries(mergedParams).forEach(([paramKey, paramValue]) => {
      if (
        removeDefaultValues &&
        defaultValues[paramKey as keyof T] === paramValue
      ) {
        return;
      }
      if (paramValue != null) {
        newSearchParams.set(paramKey, paramValue.toString());
      }
    });

    return newSearchParams.toString();
  };

  const getValidNumber = (
    key: keyof T,
    value: string | null,
    maximumValue?: number
  ): number => {
    const num = Number(value);
    if (!isNaN(num) && num > 0) {
      if (maximumValue && num > maximumValue) return maximumValue;

      return num;
    }

    return defaultValues[key] as number;
  };

  const getValidLiteral = <K extends keyof T>(
    key: K,
    value: string | null
  ): T[K] => {
    if (!value) return defaultValues[key] as T[K];

    const validValues = validTypes[key];
    if (!Array.isArray(validValues)) return defaultValues[key] as T[K];

    const typedValue = value as T[K];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const isValid = validValues.includes(typedValue as any);

    return (isValid ? typedValue : defaultValues[key]) as T[K];
  };

  const get = {
    ...defaultValues,
    ...Object.fromEntries(
      Array.from(searchParams.entries()).map(([key, value]) => {
        if (isNaN(Number(value))) {
          return [key, getValidLiteral(key as keyof T, value)];
        }

        return [key, getValidNumber(key as keyof T, value)];
      })
    ),
  } as T;

  const set = (params: Partial<T>) => {
    const queryParams = updateQueryString({ params });

    const queryString = queryParams ? `?${queryParams}` : '';

    router.push(`${pathname}${queryString}`);
  };

  return [get, set] as const;
};

export default useQueryParams;
